import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Disclosure, Dialog, Transition } from '@headlessui/react';
import { XIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { PlusIcon, MinusIcon, InformationCircleIcon } from '@heroicons/react/solid';
import ShippingOptions from './ShippingOptions';
import { Spinner } from '../../../../components/Spinner';
import ProductSelectionModal from '../../new-order-components/ProductSelectionModal';
import { useExchangeForm } from '../viewmodel/useExchangeForm';
import Modal from '../../../../components/Modal';
import { saveExchange } from '../controller/exchange-controller';
import { fetchOrderById } from '../../orders-controller/order-controller';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchSettings } from '../../settings/controller/settings-controller';

const EditExchangeForm = ({ exchangeData }) => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(2);
    const [originalOrder, setOriginalOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const isInitializedRef = useRef(false);

    // Form state
    const [customerNote, setCustomerNote] = useState(exchangeData?.description || '');
    const [shippingMethod, setShippingMethod] = useState(exchangeData?.codeLivraison || 'standard');
    const [isFreeShipping, setIsFreeShipping] = useState(exchangeData?.fraisDeLivraison === 0);
    const [additionalFees, setAdditionalFees] = useState(exchangeData?.additionalFees || 0);
    const [exchangeProducts, setExchangeProducts] = useState([]);
    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Summary calculations
    const [summary, setSummary] = useState({
        originalTotal: 0,
        keepingTotal: 0,
        newProductsTotal: 0,
        shippingCost: 0,
        additionalFees: 0,
        newTotal: 0,
        difference: 0
    });

    // Application settings state
    const [appSettings, setAppSettings] = useState({
        standardShippingCost: 8, // Default value
        expressShippingCost: 12, // Default value
    });

    // Create a function to fetch or create a mock order based on exchange data
    // Update the fetchOrCreateOrderData function to handle the API response structure

    const fetchOrCreateOrderData = async (orderId, exchangeData) => {
        try {
            // First, attempt to fetch the real order
            console.log(`Attempting to fetch order with ID: ${orderId}`);
            const orderResponse = await fetchOrderById(orderId);

            console.log('Original order API response:', orderResponse);

            // Extract order data from the correct structure
            const orderEntet = orderResponse.entet || orderResponse;
            const orderItems = orderResponse.line || orderResponse.items || [];

            // Create properly formatted order with client details from original order API
            const formattedOrder = {
                id: orderId,
                number: orderEntet.reference || `Commande-${orderId}`,
                date: orderEntet.dateBonCommande || new Date().toISOString(),
                client: `${orderEntet.clientFirstName || ''} ${orderEntet.clientLastName || ''}`.trim(),
                email: orderEntet.clientEmail || '',
                phone: orderEntet.clientNumber || '',
                address: orderEntet.clientAdresse || '',
                city: orderEntet.ville || '',
                total: orderEntet.totalTTC || 0,
                status: orderEntet.etat || 'completed',
                statusText: getOrderStatusText(orderEntet.etat || 'completed'),
                // Format items from the order data
                items: orderItems.map(item => ({
                    id: item.id,
                    productId: item.productId,
                    name: item.product?.name || 'Produit',
                    // price here is left generic; we'll compute promo-aware price later when building exchangeProducts
                    price: item.prixUnitaire || item.price || 0,
                    image: item.product?.imageUrl?.[0] || '/placeholder.jpg',
                    sku: item.product?.reference || '',
                    size: item.productVariant?.size || '',
                    color: item.productVariant?.name || '',
                    quantity: item.quantite || 1,
                    productVariantId: item.productVariantId
                }))
            };

            console.log('Formatted order data:', formattedOrder);
            return formattedOrder;
        } catch (error) {
            console.error('Error in fetchOrCreateOrderData:', error);

            // If we couldn't fetch the order, try to extract client info from exchange data
            try {
                // The exchange might contain some client info within its structure
                const exchangeInfo = exchangeData.entet || exchangeData;
                const exchangeItems = exchangeData.line || exchangeData.items || [];

                // Look for the first item that might have bonCommande with client details
                const firstItem = exchangeItems[0] || {};
                const possibleOrderDetails = firstItem.bonCommande || firstItem.bonEchange?.bonCommande;

                // Create a mock order with the best data we can find
                return {
                    id: orderId,
                    number: possibleOrderDetails?.reference || `Commande-${orderId}`,
                    date: possibleOrderDetails?.dateBonCommande || exchangeInfo.dateEchange || new Date().toISOString(),
                    client: possibleOrderDetails ?
                        `${possibleOrderDetails.clientFirstName || ''} ${possibleOrderDetails.clientLastName || ''}`.trim() :
                        'Client',
                    email: possibleOrderDetails?.clientEmail || '',
                    phone: possibleOrderDetails?.clientNumber || '',
                    address: possibleOrderDetails?.clientAdresse || 'Adresse complète',
                    city: possibleOrderDetails?.ville || 'Tunis',
                    total: exchangeInfo.totalTTC || 0,
                    status: 'completed',
                    statusText: 'Terminé',
                    items: exchangeItems.map(item => ({
                        id: item.id,
                        productId: item.productId,
                        name: item.product?.name || 'Produit',
                        price: item.prixUnitaire || 0,
                        image: item.product?.imageUrl?.[0] || '/placeholder.jpg',
                        sku: item.product?.reference || '',
                        size: item.productVariant?.size || '',
                        color: item.productVariant?.name || '',
                        quantity: item.quantite || 1,
                        productVariantId: item.productVariantId
                    }))
                };
            } catch (fallbackError) {
                console.error('Error creating fallback mock order:', fallbackError);
                // Last resort fallback with hard-coded values
                return {
                    id: orderId,
                    number: `Commande-${orderId}`,
                    date: new Date().toISOString(),
                    client: 'Client',
                    email: '',
                    phone: '',
                    address: 'Adresse complète',
                    city: 'Tunis',
                    total: 0,
                    status: 'completed',
                    statusText: 'Terminé',
                    items: []
                };
            }
        }
    };

    // Helper function to get order status text
    const getOrderStatusText = (status) => {
        const statusMap = {
            'pending': 'En attente',
            'processing': 'En traitement',
            'shipping': 'Expédié',
            'completed': 'Livré',
            'cancelled': 'Annulé'
        };

        return statusMap[status] || status;
    };

    // Load original order data on mount
    useEffect(() => {
        if (!exchangeData || isInitializedRef.current) return;

        const loadOrderData = async () => {
            try {
                setIsLoading(true);
                console.log('Loading exchange data for edit:', exchangeData);

                // Extract exchange data from the correct structure
                const exchangeInfo = exchangeData.entet || exchangeData;
                const orderId = exchangeInfo.bonCommandeId;

                if (!orderId) {
                    throw new Error('Order ID not found in exchange data');
                }

                // Use our new function to get order data
                const orderData = await fetchOrCreateOrderData(orderId, exchangeData);
                setOriginalOrder(orderData);

                // Setup exchange products based on the exchange items
                const products = [];

                // Get items from either structure
                const exchangeItems = exchangeData.line || exchangeData.items || [];

                // Process exchange items and separate them by aEchange flag
                if (exchangeItems && exchangeItems.length > 0) {
                    for (const item of exchangeItems) {
                        // Find the corresponding original order item 
                        const originalItem = orderData.items.find(oi => oi.productId === item.productId);

                        // Better image handling
                        let imageUrl = 'https://www.incathlab.com/images/products/default_product.png';

                        // Check for variant images first
                        if (item.productVariant && item.productVariant.imageUrl &&
                            Array.isArray(item.productVariant.imageUrl) &&
                            item.productVariant.imageUrl.length > 0 &&
                            item.productVariant.imageUrl[0]) {
                            imageUrl = item.productVariant.imageUrl[0];
                        }
                        // Then check for product images
                        else if (item.product && item.product.imageUrl &&
                            Array.isArray(item.product.imageUrl) &&
                            item.product.imageUrl.length > 0 &&
                            item.product.imageUrl[0]) {
                            imageUrl = item.product.imageUrl[0];
                        }
                        // Use original item image if available
                        else if (originalItem && originalItem.image) {
                            imageUrl = originalItem.image;
                        }

                        // Helper: parse flexible numbers and promo
                        const parseFlexibleNumber = (v) => {
                            if (v === null || v === undefined) return 0;
                            if (typeof v === 'number') return v;
                            const s = String(v).trim().replace(',', '.');
                            const n = parseFloat(s);
                            return isNaN(n) ? 0 : n;
                        };

                        const isTruthyFlag = (v) => {
                            if (v === true || v === 1) return true;
                            if (typeof v === 'string') {
                                const l = v.toLowerCase().trim();
                                return l === 'true' || l === '1' || l === 'yes' || l === 'y';
                            }
                            return false;
                        };

                        const getPromoPrice = (it) => {
                            const promoFlag = isTruthyFlag(it.enPromo) || isTruthyFlag(it.EnPromo) || isTruthyFlag(it.product?.enPromo) || isTruthyFlag(it.product?.EnPromo);
                            const promoCandidates = [it.prixVentePromo, it.PrixVentePromo, it.prixPromo, it.prixVentePromoVal, it.product?.prixVentePromo, it.productVariant?.prixVentePromo];
                            for (const c of promoCandidates) {
                                const n = parseFlexibleNumber(c);
                                if (n > 0 && promoFlag) return n;
                            }
                            // fallback
                            return parseFlexibleNumber(it.prixUnitaire || it.price || it.prixVenteTTC || it.product?.prixUnitaire || it.product?.price || 0);
                        };

                        // For items with aEchange = true (original products being exchanged)
                        if (item.aEchange === true) {
                            // These are original order products that the customer is exchanging
                            products.push({
                                id: item.productId,
                                name: item.product?.name || originalItem?.name || 'Produit',
                                // Use promo-aware price when available
                                price: getPromoPrice(item),
                                image: imageUrl,
                                sku: item.product?.reference || originalItem?.sku || '',
                                size: item.productVariant?.size || originalItem?.size || '',
                                color: item.productVariant?.name || originalItem?.color || '',
                                originalQuantity: originalItem?.quantity || item.quantite || 1,
                                keepQuantity: 0 // All are being exchanged in the edit mode
                            });
                        } else {
                            // For items with aEchange = false (new products being added)
                            products.push({
                                id: item.productId,
                                name: item.product?.name || 'Nouveau produit',
                                price: getPromoPrice(item),
                                image: imageUrl,
                                sku: item.product?.reference || '',
                                size: item.productVariant?.size || '',
                                color: item.productVariant?.name || '',
                                originalQuantity: 0, // These are new products, not original order products
                                keepQuantity: 0,
                                quantity: item.quantite || 1,
                                selected: true,
                                action: 'add', // Mark as new products to add
                                productVariantId: item.productVariantId
                            });
                        }
                    }
                }

                console.log('Exchange products initialized:', products);
                setExchangeProducts(products);

                // Calculate initial summary using updated products
                calculateSummary(products);

                isInitializedRef.current = true;
            } catch (error) {
                console.error('Error loading order data:', error);
                toast.error("Impossible de charger les données de la commande d'origine");
            } finally {
                setIsLoading(false);
            }
        };

        loadOrderData();
    }, [exchangeData]);

    // Create initial mock order from exchange data directly
    useEffect(() => {
        if (!exchangeData || originalOrder) return;

        // Extract data from either structure
        const exchangeInfo = exchangeData.entet || exchangeData;
        const exchangeItems = exchangeData.line || exchangeData.items;

        // Create a better mock order with more realistic client details
        const mockOrderFromProps = {
            id: exchangeInfo.bonCommandeId,
            number: `Commande-${exchangeInfo.bonCommandeId}`,
            date: exchangeInfo.dateEchange || new Date().toISOString(),
            client: exchangeInfo.clientName || 'Client', // Try to get client name
            email: exchangeInfo.clientEmail || '',
            phone: exchangeInfo.clientPhone || '',
            address: exchangeInfo.clientAddress || 'Adresse de livraison normale',
            city: exchangeInfo.ville || 'Tunis',
            total: exchangeInfo.totalTTC || 0,
            status: 'completed',
            statusText: 'Terminé',
            items: exchangeItems.map(item => ({
                id: item.id,
                productId: item.productId,
                name: item.product?.name || 'Produit',
                price: item.prixUnitaire || 0,
                image: item.product?.imageUrl?.[0] || '/placeholder.jpg',
                sku: item.product?.reference || '',
                size: item.productVariant?.size || '',
                color: item.productVariant?.name || '',
                quantity: item.quantite || 1,
                productVariantId: item.productVariantId
            }))
        };

        // Set the mock order immediately without waiting for API
        setOriginalOrder(mockOrderFromProps);

        // Calculate summary with this initial data
        if (mockOrderFromProps && exchangeProducts.length > 0) {
            calculateSummary(exchangeProducts);
        }
    }, [exchangeData, originalOrder, exchangeProducts]);

    // Load application settings when component mounts
    useEffect(() => {
        const fetchSettingsApi = async () => {
            try {
                const settings = await fetchSettings();
                console.log('Loaded application settings:', settings.fraisDeLivraison);

                // Check if settings contains shipping costs and update state accordingly
                if (settings) {
                    setAppSettings({
                        standardShippingCost: settings.fraisDeLivraison,
                    });
                }
            } catch (error) {
                console.error('Error loading application settings:', error);
                // Keep default values if there's an error
            }
        };

        fetchSettingsApi();
    }, []);

    // Calculate summary values
    const calculateSummary = (products) => {
        const originalTotal = originalOrder?.total || 0;

        // Calculate totals for products that are kept from the original order
        // This includes:
        // 1. Products that are selected with keepQuantity > 0 (partial keep)
        // 2. Products that are NOT selected at all (fully kept) - THIS IS THE KEY FIX
        const keepingTotal = products.reduce((sum, p) => {
            // Only consider original order products (not new products)
            if (p.originalQuantity > 0) {
                if (p.selected) {
                    // If selected, use keepQuantity
                    return sum + (p.price * p.keepQuantity);
                } else {
                    // If NOT selected, the entire quantity is kept - THIS IS THE IMPORTANT PART
                    return sum + (p.price * p.originalQuantity);
                }
            }
            return sum;
        }, 0);

        // Calculate total for new products being added
        const newProductsTotal = products
            .filter(p => p.action === 'add')
            .reduce((sum, p) => sum + (p.price * p.quantity), 0);

        const shippingCost = isFreeShipping ? 0 : (shippingMethod === 'express' ? appSettings.expressShippingCost : appSettings.standardShippingCost);

        const newTotal = keepingTotal + newProductsTotal + shippingCost + additionalFees;

        const difference = newTotal - originalTotal;

        console.log('=== SUMMARY CALCULATION DEBUG ===');
        console.log('Original total:', originalTotal);
        console.log('Products breakdown:');
        products.forEach(p => {
            if (p.originalQuantity > 0) {
                const keptQty = p.selected ? p.keepQuantity : p.originalQuantity;
                const keptValue = p.price * keptQty;
                console.log(`  ${p.name}: ${p.selected ? 'SELECTED' : 'NOT SELECTED'} - Keeping ${keptQty}/${p.originalQuantity} = ${keptValue.toFixed(2)} TND`);
            }
        });
        console.log('Keeping total:', keepingTotal);
        console.log('New products total:', newProductsTotal);
        console.log('Shipping cost:', shippingCost);
        console.log('New total:', newTotal);
        console.log('Difference:', difference);

        setSummary({
            originalTotal,
            keepingTotal,
            newProductsTotal,
            shippingCost,
            additionalFees,
            newTotal,
            difference
        });
    };

    // Recalculate summary when related values change
    useEffect(() => {
        if (originalOrder) {
            calculateSummary(exchangeProducts);
        }
    }, [originalOrder, exchangeProducts, shippingMethod, isFreeShipping, additionalFees]);

    // Handle quantity change for exchange products
    const handleQuantityChange = (product, direction, field) => {
        setExchangeProducts(prev => prev.map(p => {
            if (p.id === product.id) {
                if (field === 'keep') {
                    // For original products, modify the keepQuantity
                    let newKeepQuantity = p.keepQuantity;

                    if (direction === 'increase') {
                        if (newKeepQuantity < p.originalQuantity) {
                            newKeepQuantity += 1;
                        }
                    } else if (direction === 'decrease') {
                        if (newKeepQuantity > 0) {
                            newKeepQuantity -= 1;
                        }
                    }

                    return {
                        ...p,
                        keepQuantity: newKeepQuantity,
                        action: newKeepQuantity === p.originalQuantity ? 'keep' :
                            newKeepQuantity === 0 ? 'exchange' : 'partial'
                    };
                } else if (field === 'quantity') {
                    // For new products, modify the quantity
                    let newQuantity = p.quantity || 1;

                    if (direction === 'increase') {
                        newQuantity += 1;
                    } else if (direction === 'decrease') {
                        if (newQuantity > 1) {
                            newQuantity -= 1;
                        }
                    }

                    return {
                        ...p,
                        quantity: newQuantity
                    };
                }
            }
            return p;
        }));
    };

    // Add product to exchange
    const handleAddToExchange = (productId) => {
        // Find the product in the original order
        const originalProduct = originalOrder.items.find(item => item.productId === productId);

        if (!originalProduct) return;

        // Check if this product is already in the exchange products list
        const existingProduct = exchangeProducts.find(p => p.id === productId);

        if (existingProduct) {
            // Update existing product to be selected with 0 keep quantity (full exchange)
            setExchangeProducts(prev => prev.map(p => {
                if (p.id === productId) {
                    return {
                        ...p,
                        selected: true,
                        keepQuantity: 0,
                        action: 'exchange'
                    };
                }
                return p;
            }));
        } else {
            // Add new product to exchange list
            // Compute promo-aware price for the original product when adding it
            const parseFlexibleNumber = (v) => {
                if (v === null || v === undefined) return 0;
                if (typeof v === 'number') return v;
                const s = String(v).trim().replace(',', '.');
                const n = parseFloat(s);
                return isNaN(n) ? 0 : n;
            };

            const isTruthyFlag = (v) => {
                if (v === true || v === 1) return true;
                if (typeof v === 'string') {
                    const l = v.toLowerCase().trim();
                    return l === 'true' || l === '1' || l === 'yes' || l === 'y';
                }
                return false;
            };

            const getPromoPrice = (it) => {
                const promoFlag = isTruthyFlag(it.enPromo) || isTruthyFlag(it.EnPromo) || isTruthyFlag(it.product?.enPromo) || isTruthyFlag(it.product?.EnPromo);
                const promoCandidates = [it.prixVentePromo, it.PrixVentePromo, it.prixPromo, it.product?.prixVentePromo, it.productVariant?.prixVentePromo];
                for (const c of promoCandidates) {
                    const n = parseFlexibleNumber(c);
                    if (n > 0 && promoFlag) return n;
                }
                return parseFlexibleNumber(it.price || it.prixUnitaire || it.prixVenteTTC || it.product?.price || 0);
            };

            const newProduct = {
                id: productId,
                name: originalProduct.name,
                price: getPromoPrice(originalProduct),
                image: originalProduct.image || '/placeholder.jpg',
                sku: originalProduct.sku || '',
                size: originalProduct.size || '',
                color: originalProduct.color || '',
                originalQuantity: originalProduct.quantity,
                keepQuantity: 0, // Exchange all items by default
                quantity: originalProduct.quantity,
                selected: true,
                action: 'exchange',
                productVariantId: originalProduct.productVariantId
            };

            setExchangeProducts(prev => [...prev, newProduct]);
        }

        toast.success(`Produit "${originalProduct.name}" ajouté à l'échange`);
    };

    // Remove product from exchange
    const handleRemoveFromExchange = (productId) => {
        setExchangeProducts(prev => prev.map(p => {
            if (p.id === productId) {
                if (p.originalQuantity > 0) {
                    // For original products, mark as not selected and keep all
                    return {
                        ...p,
                        selected: false,
                        keepQuantity: p.originalQuantity,
                        action: 'keep'
                    };
                } else {
                    // For newly added products, mark for removal
                    return {
                        ...p,
                        selected: false
                    };
                }
            }
            return p;
        }));
    };

    // Add new product
    // Update the handleProductSelect function with better console logging and debugging

    // Add new product with debugging
    // Update the handleProductSelect function to handle array input

    // Update the handleProductSelect function to handle multiple products

    const handleProductSelect = (productData, quantity = 1) => {
        console.log('Raw product data from modal:', productData);
        console.log('Product structure:', JSON.stringify(productData, null, 2));

        // Convert to array if it's not already
        const productsArray = Array.isArray(productData) ? productData : [productData];

        if (productsArray.length === 0) {
            console.error('No valid product data found');
            toast.error('Erreur lors de l\'ajout du produit, données manquantes');
            return;
        }

        // Process each product in the array
        const newProducts = productsArray.map(product => {
            console.log('Processing product:', product);

            // For variants, we need to check if the product has a variant property
            const hasVariant = product.isVariant && product.variant && product.variant.id;
            const variant = hasVariant ? product.variant : null;

            // Helper: parse numbers that may use comma as decimal separator
            const parseFlexibleNumber = (v) => {
                if (v === null || v === undefined) return 0;
                if (typeof v === 'number') return v;
                const s = String(v).trim().replace(',', '.');
                const n = parseFloat(s);
                return isNaN(n) ? 0 : n;
            };

            const isTruthyFlag = (v) => {
                if (v === true || v === 1) return true;
                if (typeof v === 'string') {
                    const l = v.toLowerCase().trim();
                    return l === 'true' || l === '1' || l === 'yes' || l === 'y';
                }
                return false;
            };

            // Prefer promo price when enPromo flag is present and promo price > 0
            const promoFlag = isTruthyFlag(product.enPromo) || isTruthyFlag(product.EnPromo) || isTruthyFlag(product.product?.enPromo) || isTruthyFlag(product.product?.EnPromo) || isTruthyFlag(variant?.enPromo);
            const promoCandidates = [product.prixVentePromo, product.PrixVentePromo, product.prixPromo, product.prixVentePromoVal, product.product?.prixVentePromo, variant?.prixVentePromo, variant?.price];
            let extractedPrice = 0;
            if (promoFlag) {
                for (const c of promoCandidates) {
                    const n = parseFlexibleNumber(c);
                    if (n > 0) {
                        extractedPrice = n;
                        break;
                    }
                }
            }

            // Fallbacks when no promo price found or promo flag not present
            if (!extractedPrice) {
                extractedPrice = parseFlexibleNumber(product.price) || parseFlexibleNumber(product.prixVenteTTC) || parseFlexibleNumber(variant?.price) || parseFlexibleNumber(product.prixVenteHt) || 0;
            }

            console.log('Extracted price (promo-aware):', extractedPrice);

            // Extract image URL - improved to better handle variants
            let imageUrl = '/placeholder.jpg';

            // Check for variant images first
            if (hasVariant && variant.imageUrl && variant.imageUrl.length > 0 && variant.imageUrl[0]) {
                imageUrl = variant.imageUrl[0];
                console.log('Using variant image:', imageUrl);
            }
            // Then check for product images
            else if (Array.isArray(product.imageUrl) && product.imageUrl.length > 0 && product.imageUrl[0]) {
                imageUrl = product.imageUrl[0];
                console.log('Using product image array:', imageUrl);
            }
            // Handle string image URLs
            else if (typeof product.imageUrl === 'string' && product.imageUrl) {
                imageUrl = product.imageUrl;
                console.log('Using product image string:', imageUrl);
            }
            // Check for image property
            else if (product.image) {
                imageUrl = product.image;
                console.log('Using product.image:', imageUrl);
            }

            // Extract variant details
            let variantId = product.variantId || null;
            let size = '';
            let color = '';

            // Extract variant information
            if (hasVariant) {
                console.log('Using variant object:', variant);
                variantId = variant.id;

                // Try to parse variant name
                if (variant.name) {
                    const parts = variant.name.split(' - ');
                    if (parts.length >= 2) {
                        // Try to identify color terms
                        const colorTerms = ['blanc', 'noir', 'rouge', 'bleu', 'vert', 'jaune', 'orange', 'marron', 'gris'];

                        // Look for color terms in parts
                        parts.forEach(part => {
                            const lowerPart = part.toLowerCase();
                            if (colorTerms.some(color => lowerPart.includes(color))) {
                                color = part;
                            } else if (!isNaN(part)) {
                                // Numeric part is likely a size
                                size = part;
                            }
                        });
                    }
                }
            } else if (product.name) {
                // Try to extract info from product name if it's in format "Product - Color - Size"
                const nameParts = product.name.split(' - ');
                if (nameParts.length >= 2) {
                    // Try to extract size and color from product name
                    const colorTerms = ['blanc', 'noir', 'rouge', 'bleu', 'vert', 'jaune', 'orange', 'marron', 'gris'];

                    nameParts.forEach(part => {
                        const lowerPart = part.toLowerCase();
                        if (colorTerms.some(colorTerm => lowerPart.includes(colorTerm))) {
                            color = part;
                        } else if (!isNaN(part)) {
                            size = part;
                        }
                    });
                }
            }

            console.log(`Extracted variant details - ID: ${variantId}, Size: ${size}, Color: ${color}`);

            // Create complete product object
            return {
                id: product.id || product.productId,
                name: product.name || 'Produit',
                price: extractedPrice,
                image: imageUrl,
                sku: product.reference || product.sku || '',
                size: size || product.size || '',
                color: color || product.color || '',
                originalQuantity: 0,
                keepQuantity: 0,
                quantity: quantity,
                selected: true,
                action: 'add',
                productVariantId: variantId
            };
        });

        console.log('All formatted product objects:', newProducts);

        // Add all new products to the exchange products list
        setExchangeProducts(prev => [...prev, ...newProducts]);
        setShowAddProductModal(false);

        // Show appropriate success message
        if (newProducts.length === 1) {
            toast.success(`Produit "${newProducts[0].name}" ajouté avec succès`);
        } else {
            toast.success(`${newProducts.length} produits ajoutés avec succès`);
        }
    };

    // Remove new product
    const handleRemoveProduct = (productId) => {
        setExchangeProducts(prev => prev.filter(p => p.id !== productId));
        toast.success('Produit supprimé');
    };

    // Handle next step
    const handleNext = () => {
        if (currentStep === 2) {
            // Only validate that we have exchange products, not reason anymore
            const hasExchangeProducts = exchangeProducts.some(p =>
                (p.originalQuantity > 0 && p.keepQuantity < p.originalQuantity) || p.action === 'add'
            );

            if (!hasExchangeProducts) {
                toast.error("Veuillez sélectionner au moins un produit à échanger ou ajouter un nouveau produit");
                return;
            }
        }

        // Move to next step
        setCurrentStep(prev => prev + 1);
    };

    // Handle back
    const handleBack = () => {
        if (currentStep > 2) {
            setCurrentStep(prev => prev - 1);
        } else {
            navigate('/admin/exchanges');
        }
    };

    // Submit exchange update
    // Update the handleSubmitExchange function

    // Submit exchange update
    // Submit exchange update
    const handleSubmitExchange = async () => {
        try {
            setIsSubmitting(true);

            // Debug: Log current exchangeProducts to understand the structure
            console.log('Current exchangeProducts:', exchangeProducts);

            // Format the exchange data according to the expected API structure
            const formattedExchangeData = {
                entet: {
                    id: exchangeData.id,
                    bonCommandeId: originalOrder.id,
                    dateEchange: new Date().toISOString(),
                    reference: exchangeData.reference || "",
                    description: customerNote,
                    codeLivraison: shippingMethod,
                    fraisDeLivraison: isFreeShipping ? 0 : (shippingMethod === 'express' ? appSettings.expressShippingCost : appSettings.standardShippingCost),
                    totalTTC: summary.newTotal,
                    totalHt: summary.newTotal / 1.19,
                    totalTva: summary.newTotal - (summary.newTotal / 1.19),
                    totalRemise: 0,
                    resteAPayer: summary.difference > 0 ? summary.difference : 0,
                    etat: exchangeData.etat || "pending"
                },
                line: [
                    // ONLY products being exchanged (returned to you) - ONLY exchange quantity
                    ...exchangeProducts
                        .filter(p => {
                            // Only include products where some quantity is being exchanged
                            const hasOriginalQuantity = p.originalQuantity > 0;
                            const exchangeQuantity = p.originalQuantity - p.keepQuantity;
                            const isBeingExchanged = exchangeQuantity > 0;
                            
                            console.log(`Product ${p.name}: originalQty=${p.originalQuantity}, keepQty=${p.keepQuantity}, exchangeQty=${exchangeQuantity}, willInclude=${hasOriginalQuantity && isBeingExchanged}`);
                            
                            return hasOriginalQuantity && isBeingExchanged;
                        })
                        .map(p => {
                            const exchangeQuantity = p.originalQuantity - p.keepQuantity;
                            console.log(`Creating exchange line for ${p.name} with exchange quantity: ${exchangeQuantity}`);

                            // Ensure prixUnitaire uses promo-aware price if available (p.price already computed as such)
                            const unitPrice = parseFloat(p.price) || 0;
                            const totalTTC = unitPrice * exchangeQuantity;

                            return {
                                productId: p.id,
                                productVariantId: p.productVariantId,
                                quantite: exchangeQuantity, // ONLY the quantity being exchanged
                                prixUnitaire: unitPrice,
                                totalTTC: totalTTC,
                                totalHt: totalTTC / 1.19,
                                totalTva: totalTTC - (totalTTC / 1.19),
                                totalRemise: 0,
                                tauxTva: 19,
                                tauxRemise: 0,
                                aEchange: true // This product is being returned/exchanged
                            };
                        }),
                    // ONLY new products being added (replacement products)
                    ...exchangeProducts
                        .filter(p => {
                            const isNewProduct = p.action === 'add';
                            console.log(`Product ${p.name}: action=${p.action} -> isNewProduct: ${isNewProduct}`);
                            return isNewProduct;
                        })
                        .map(p => {
                            console.log(`Creating new product line for ${p.name} with quantity: ${p.quantity}`);
                            const unitPrice = parseFloat(p.price) || 0;
                            const totalTTC = unitPrice * (p.quantity || 1);

                            return {
                                productId: p.id,
                                productVariantId: p.productVariantId,
                                quantite: p.quantity,
                                prixUnitaire: unitPrice,
                                totalTTC: totalTTC,
                                totalHt: totalTTC / 1.19,
                                totalTva: totalTTC - (totalTTC / 1.19),
                                totalRemise: 0,
                                tauxTva: 19,
                                tauxRemise: 0,
                                aEchange: false // This is a new product being given in exchange
                            };
                        })
                ]
            };

            console.log('Final exchange data being sent:', formattedExchangeData);
            console.log('Products being returned (aEchange: true):', formattedExchangeData.line.filter(p => p.aEchange === true));
            console.log('New products being given (aEchange: false):', formattedExchangeData.line.filter(p => p.aEchange === false));
            console.log('Total line items:', formattedExchangeData.line.length);

            // Call the saveExchange function which handles both create and update
            const result = await saveExchange(formattedExchangeData);

            console.log('Exchange saved:', result);
            toast.success("Échange mis à jour avec succès");

            // Navigate back to exchanges list
            navigate('/admin/exchanges');
        } catch (error) {
            console.error('Error saving exchange:', error);
            toast.error(error.message || "Une erreur est survenue lors de l'enregistrement de l'échange");
        } finally {
            setIsSubmitting(false);
            setShowConfirmModal(false);
        }
    };

    // Format price helper
    const formatPrice = (price) => {
        if (price === null || price === undefined) {
            return '0.00 TND';
        }
        return `${parseFloat(price).toFixed(2)} TND`;
    };

    // Render based on current step
    const renderStep = () => {
        switch (currentStep) {
            case 2:
                return (
                    <div className="space-y-8">
                        <div>
                            <h2 className="text-lg font-medium text-gray-900">Modifier l'échange</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Modifiez les produits à échanger et les détails de l'échange.
                            </p>
                        </div>

                        {/* Original Order Details Section (Collapsible) */}
                        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
                            <Disclosure defaultOpen={true}>
                                {({ open }) => (
                                    <>
                                        <Disclosure.Button className="w-full px-4 py-5 sm:px-6 text-left flex justify-between items-center bg-gray-50">
                                            <div>
                                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                    Commande originale: {originalOrder?.number || exchangeData.bonCommandeId}
                                                </h3>
                                                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                                    {originalOrder?.date && new Date(originalOrder.date).toLocaleDateString('fr-FR', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </p>
                                            </div>
                                            <div className="flex items-center">
                                                <span className="text-indigo-600 text-sm mr-3">
                                                    {open ? 'Masquer les détails' : 'Afficher les détails'}
                                                </span>
                                                <ChevronDownIcon
                                                    className={`${open ? 'transform rotate-180' : ''
                                                        } h-5 w-5 text-indigo-500`}
                                                />
                                            </div>
                                        </Disclosure.Button>
                                        <Disclosure.Panel>
                                            {originalOrder ? (
                                                <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                                                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Client</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">
                                                                {originalOrder.client || 'Client'}<br />
                                                                {originalOrder.email || 'exemple@email.com'}<br />
                                                                {originalOrder.phone || '+216 XX XXX XXX'}
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Adresse de livraison</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">
                                                                {originalOrder.address || 'Adresse du client'}<br />
                                                                {originalOrder.city || 'Tunis'}<br />
                                                            </dd>
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            <dt className="text-sm font-medium text-gray-500">Produits de la commande originale</dt>
                                                            <dd className="mt-3">
                                                                {originalOrder.items && originalOrder.items.length > 0 ? (
                                                                    <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
                                                                        <table className="min-w-full divide-y divide-gray-200">
                                                                            <thead className="bg-gray-50">
                                                                                <tr>
                                                                                    <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                        <span className="sr-only">Sélectionner</span>
                                                                                    </th>
                                                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produit</th>
                                                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                                                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité</th>
                                                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                                                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                                {originalOrder.items.map((item, index) => {
                                                                                    // Find if this product is already in the exchange list
                                                                                    const exchangeItem = exchangeProducts.find(p => p.id === item.productId);
                                                                                    const isSelected = exchangeItem && exchangeItem.selected;

                                                                                    return (
                                                                                        <tr key={item.id || index}>
                                                                                            <td className="px-2 py-4 whitespace-nowrap">
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                                                                    checked={isSelected}
                                                                                                    onChange={() => {
                                                                                                        if (isSelected) {
                                                                                                            handleRemoveFromExchange(item.productId);
                                                                                                        } else {
                                                                                                            handleAddToExchange(item.productId);
                                                                                                        }
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                            <td className="px-4 py-4 whitespace-nowrap">
                                                                                                <div className="flex items-center">
                                                                                                    <div className="h-10 w-10 flex-shrink-0">
                                                                                                        <img
                                                                                                            className="h-10 w-10 rounded-md object-cover"
                                                                                                            src={item.image || 'https://www.incathlab.com/images/products/default_product.png'}
                                                                                                            alt={item.name || 'Produit'}
                                                                                                            onError={(e) => {
                                                                                                                e.target.onerror = null;
                                                                                                                e.target.src = 'https://www.incathlab.com/images/products/default_product.png';
                                                                                                            }}
                                                                                                        />
                                                                                                    </div>
                                                                                                    <div className="ml-4">
                                                                                                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                                                                                        <div className="text-sm text-gray-500">{item.sku}</div>
                                                                                                        {(item.size || item.color) && (
                                                                                                            <div className="text-sm text-gray-500">
                                                                                                                {item.size && `Taille: ${item.size}`}
                                                                                                                {item.size && item.color && ' | '}
                                                                                                                {item.color && `Couleur: ${item.color}`}
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                            </td>
                                                                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatPrice(item.price)}</td>
                                                                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                                                {exchangeItem ? (
                                                                                                    <div className="flex items-center space-x-2">
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="p-1 rounded-full text-gray-400 hover:text-gray-500"
                                                                                                            onClick={() => handleQuantityChange(exchangeItem, 'decrease', 'keep')}
                                                                                                        >
                                                                                                            <MinusIcon className="h-4 w-4" />
                                                                                                        </button>
                                                                                                        <span className="text-sm text-gray-700">
                                                                                                            {exchangeItem.keepQuantity} / {item.quantity}
                                                                                                        </span>
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="p-1 rounded-full text-gray-400 hover:text-gray-500"
                                                                                                            onClick={() => handleQuantityChange(exchangeItem, 'increase', 'keep')}
                                                                                                            disabled={exchangeItem.keepQuantity >= item.quantity}
                                                                                                        >
                                                                                                            <PlusIcon className="h-4 w-4" />
                                                                                                        </button>
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    item.quantity
                                                                                                )}
                                                                                            </td>
                                                                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                                                {item.stock || "N/A"}
                                                                                            </td>
                                                                                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                                                                                {isSelected ? (
                                                                                                    <span className="text-green-600">À échanger</span>
                                                                                                ) : (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => handleAddToExchange(item.productId)}
                                                                                                        className="text-indigo-600 hover:text-indigo-900"
                                                                                                    >
                                                                                                        Ajouter à l'échange
                                                                                                    </button>
                                                                                                )}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-sm text-gray-500">Aucun produit trouvé dans la commande originale.</p>
                                                                )}
                                                            </dd>
                                                            <div className="mt-4">
                                                                <dt className="text-sm font-medium text-gray-500">Résumé de la commande</dt>
                                                                <dd className="mt-1 text-sm text-gray-900">
                                                                    <div className="flex justify-between">
                                                                        <span>Nombre d'articles:</span>
                                                                        <span>{originalOrder.items?.length || 0}</span>
                                                                    </div>
                                                                    <div className="flex justify-between font-medium mt-1">
                                                                        <span>Total:</span>
                                                                        <span>{formatPrice(originalOrder.total)}</span>
                                                                    </div>
                                                                </dd>
                                                            </div>
                                                        </div>
                                                    </dl>
                                                </div>
                                            ) : (
                                                <div className="px-4 py-5 sm:px-6 text-center">
                                                    <p className="text-sm text-gray-500">
                                                        Chargement des détails de la commande...
                                                    </p>
                                                </div>
                                            )}
                                        </Disclosure.Panel>
                                    </>
                                )}
                            </Disclosure>
                        </div>

                        {/* Products to Exchange Section */}
                        <div id="exchange-products-section" className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">Produits de la commande originale</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Produits sélectionnés pour l'échange.
                                </p>
                                <div className="mt-5">
                                    {exchangeProducts.filter(p => p.originalQuantity > 0 && p.selected).length > 0 ? (
                                        <div className="flex flex-col">
                                            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                                    <div className="overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                            <thead className="bg-gray-50">
                                                                <tr>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Produit
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Prix unitaire
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Quantité d'origine
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        À garder
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        À échanger
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Action
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                {exchangeProducts.filter(p => p.originalQuantity > 0 && p.selected).map((product) => (
                                                                    <tr key={product.id} id={`exchange-item-${product.id}`} className="transition-all duration-300">
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="flex items-center">
                                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                                    <img
                                                                                        className="h-10 w-10 rounded-md object-cover"
                                                                                        src={product.image || 'https://www.incathlab.com/images/products/default_product.png'}
                                                                                        alt={product.name || 'Produit'}
                                                                                        onError={(e) => {
                                                                                            e.target.onerror = null;
                                                                                            e.target.src = 'https://www.incathlab.com/images/products/default_product.png';
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                <div className="ml-4">
                                                                                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                                                    <div className="text-sm text-gray-500">
                                                                                        {product.sku} {product.size && `| ${product.size}`} {product.color && `| ${product.color}`}
                                                                                        {!product.sku && !product.size && !product.color && 'Détails non disponibles'}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="text-sm text-gray-900">{formatPrice(product.price)}</div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="text-sm text-gray-900">{product.originalQuantity}</div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="flex items-center">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleQuantityChange(product, 'decrease', 'keep')}
                                                                                    className="text-white bg-gray-400 hover:bg-gray-500 p-1 rounded-l-md"
                                                                                    disabled={product.keepQuantity <= 0}
                                                                                >
                                                                                    <MinusIcon className="h-4 w-4" />
                                                                                </button>
                                                                                <span className="px-4 py-1 bg-gray-100 text-gray-900">
                                                                                    {product.keepQuantity}
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleQuantityChange(product, 'increase', 'keep')}
                                                                                    className="text-white bg-gray-400 hover:bg-gray-500 p-1 rounded-r-md"
                                                                                    disabled={product.keepQuantity >= product.originalQuantity}
                                                                                >
                                                                                    <PlusIcon className="h-4 w-4" />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="text-sm text-gray-900">
                                                                                {product.originalQuantity - product.keepQuantity}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleRemoveFromExchange(product.id)}
                                                                                className="text-red-600 hover:text-red-900"
                                                                            >
                                                                                Supprimer
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 p-8 text-center rounded-lg">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2H5a2 2 0 00-2 2v2M7 7h10" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun produit sélectionné</h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Ajoutez des produits à l'échange.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* New Products Section */}
                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900">Nouveaux produits à ajouter</h3>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddProductModal(true)}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                                        Ajouter un produit
                                    </button>
                                </div>
                                <div className="mt-5">
                                    {exchangeProducts.filter(p => p.action === 'add').length > 0 ? (
                                        <div className="flex flex-col">
                                            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                                    <div className="overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                            <thead className="bg-gray-50">
                                                                <tr>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Produit
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Prix unitaire
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Quantité
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Total
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                    >
                                                                        Action
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                {exchangeProducts.filter(p => p.action === 'add').map((product) => (
                                                                    <tr key={`new-${product.id}`}>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="flex items-center">
                                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                                    <img
                                                                                        className="h-10 w-10 rounded-md object-cover"
                                                                                        src={product.image || 'https://www.incathlab.com/images/products/default_product.png'}
                                                                                        alt={product.name || 'Produit'}
                                                                                        onError={(e) => {
                                                                                            e.target.onerror = null;
                                                                                            e.target.src = 'https://www.incathlab.com/images/products/default_product.png';
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                <div className="ml-4">
                                                                                    <div className="text-sm font-medium text-gray-900">{product.name || 'Produit sans nom'}</div>
                                                                                    <div className="text-sm text-gray-500">
                                                                                        {product.sku && `${product.sku}`}
                                                                                        {product.size && ` | Taille: ${product.size}`}
                                                                                        {product.color && ` | Couleur: ${product.color}`}
                                                                                        {!product.sku && !product.size && !product.color && 'Détails non disponibles'}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="text-sm text-gray-900">
                                                                                {product.price ? formatPrice(product.price) : '0.00 TND'}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="flex items-center">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleQuantityChange(product, 'decrease', 'quantity')}
                                                                                    className="text-white bg-gray-400 hover:bg-gray-500 p-1 rounded-l-md"
                                                                                    disabled={product.quantity <= 1}
                                                                                >
                                                                                    <MinusIcon className="h-4 w-4" />
                                                                                </button>
                                                                                <span className="px-4 py-1 bg-gray-100 text-gray-900">
                                                                                    {product.quantity || 1}
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleQuantityChange(product, 'increase', 'quantity')}
                                                                                    className="text-white bg-gray-400 hover:bg-gray-500 p-1 rounded-r-md"
                                                                                >
                                                                                    <PlusIcon className="h-4 w-4" />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                            <div className="text-sm font-medium text-gray-900">
                                                                                {formatPrice((product.price || 0) * (product.quantity || 1))}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleRemoveProduct(product.id)}
                                                                                className="text-red-600 hover:text-red-900"
                                                                            >
                                                                                Supprimer
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 p-8 text-center rounded-lg">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun nouveau produit</h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Ajoutez de nouveaux produits pour compléter l'échange.
                                            </p>
                                            <div className="mt-6">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddProductModal(true)}
                                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                                                    Ajouter un produit
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Exchange Details Section - Enhanced Design */}
                        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
                            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 to-indigo-100 border-b border-indigo-100">
                                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-4 4a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    Détails de l'échange
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Ajoutez une description détaillée pour expliquer le motif de cet échange et toutes informations importantes.
                                </p>
                            </div>
                            <div className="px-6 py-6">
                                <div className="grid grid-cols-1 gap-y-6 gap-x-4">
                                    <div>
                                        <label htmlFor="customer-note" className="block text-sm font-medium text-gray-700 mb-1">
                                            Description de l'échange
                                        </label>
                                        <div className="mt-1 relative">
                                            <textarea
                                                id="customer-note"
                                                name="customer-note"
                                                rows={4}
                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-3 pr-10 py-2 transition-all duration-200 ease-in-out resize-y"
                                                placeholder="Décrivez le motif de l'échange, les problèmes rencontrés..."
                                                value={customerNote}
                                                onChange={(e) => setCustomerNote(e.target.value)}
                                                style={{ minHeight: "100px" }}
                                            />
                                            <div className="absolute right-2 top-2 pointer-events-none text-indigo-500">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                </svg>
                                            </div>
                                            <div className={`absolute bottom-2 right-2 text-xs ${customerNote.length > 500 ? 'text-red-500' : 'text-gray-400'}`}>
                                                {customerNote.length}/500
                                            </div>
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500 flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-indigo-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            Cette description sera utilisée pour le traitement et l'historique de l'échange
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-8">
                        <div>
                            <h2 className="text-lg font-medium text-gray-900">Finaliser l'échange</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Vérifiez les détails et configurez les options d'expédition et de paiement.
                            </p>
                        </div>

                        {/* Shipping Options Section */}
                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">

                                <ShippingOptions
                                    shippingMethod={shippingMethod}
                                    setShippingMethod={setShippingMethod}
                                    isFreeShipping={isFreeShipping}
                                    setIsFreeShipping={setIsFreeShipping}
                                    appSettings={appSettings} // Pass the appSettings with shipping cost
                                />
                            </div>
                        </div>

                        {/* Summary Section */}
                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">Résumé de l'échange</h3>
                                <div className="mt-5 border-t border-gray-200 pt-4">
                                    <dl className="divide-y divide-gray-200">
                                        <div className="py-3 flex justify-between text-sm font-medium">
                                            <dt className="text-gray-500">Montant commande originale</dt>
                                            <dd className="text-gray-900">{formatPrice(summary.originalTotal)}</dd>
                                        </div>
                                        <div className="py-3 flex justify-between text-sm font-medium">
                                            <dt className="text-gray-500">Produits conservés</dt>
                                            <dd className="text-gray-900">{formatPrice(summary.keepingTotal || 0)}</dd>
                                        </div>
                                        <div className="py-3 flex justify-between text-sm font-medium">
                                            <dt className="text-gray-500">Nouveaux produits</dt>
                                            <dd className="text-gray-900">{formatPrice(summary.newProductsTotal || 0)}</dd>
                                        </div>
                                        <div className="py-3 flex justify-between text-sm font-medium">
                                            <dt className="text-gray-500">Frais de livraison</dt>
                                            <dd className="text-gray-900">{formatPrice(summary.shippingCost || 0)}</dd>
                                        </div>
                                        <div className="py-3 flex justify-between text-sm font-medium">
                                            <dt className="text-gray-500">Frais supplémentaires</dt>
                                            <dd className="text-gray-900">{formatPrice(summary.additionalFees)}</dd>
                                        </div>
                                        <div className="py-3 flex justify-between text-sm font-medium">
                                            <dt className="text-gray-900 font-bold">Nouveau total</dt>
                                            <dd className="text-gray-900 font-bold">{formatPrice(summary.newTotal)}</dd>
                                        </div>
                                        <div className="py-3 flex justify-between text-sm font-medium">
                                            <dt className="text-gray-900">Différence</dt>
                                            <dd className={`font-medium ${summary.difference > 0 ? 'text-green-600' : summary.difference < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                                {summary.difference > 0 ? '+' : ''}{formatPrice(summary.difference)}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>

                                {summary.difference !== 0 && (
                                    <div className={`mt-4 rounded-md ${summary.difference > 0 ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'} p-4`}>
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <InformationCircleIcon className="h-5 w-5" aria-hidden="true" />
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium">
                                                    {summary.difference > 0
                                                        ? 'Paiement supplémentaire requis'
                                                        : 'Remboursement nécessaire'}
                                                </h3>
                                                <div className="mt-2 text-sm">
                                                    <p>
                                                        {summary.difference > 0
                                                            ? `Le client devra payer un montant supplémentaire de ${formatPrice(summary.difference)}.`
                                                            : `Le client devra être remboursé de ${formatPrice(Math.abs(summary.difference))}.`}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // Main component render
    return (
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 py-4">


            {/* Progress steps */}
            <nav aria-label="Progress" className="mb-8">
                <ol className="flex items-center justify-between">
                    <li className="relative flex items-center mb-8 sm:mb-0 sm:w-1/2">
                        <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full ${currentStep === 2
                                ? 'bg-indigo-600 text-white'
                                : currentStep > 2
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white border-2 border-gray-300 text-gray-500'
                                }`}
                        >
                            {currentStep > 2 ? (
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            ) : (
                                <span>1</span>
                            )}
                        </div>
                        <span className={`ml-3 text-sm font-medium ${currentStep === 2 ? 'text-indigo-600' : currentStep > 2 ? 'text-indigo-600' : 'text-gray-500'
                            }`}>
                            Modifier l'échange
                        </span>

                        {/* Connector */}
                        {currentStep > 2 && (
                            <div className="hidden sm:block absolute right-0 h-0.5 w-1/3 bg-indigo-600" style={{ left: '70%' }} aria-hidden="true" />
                        )}
                        {currentStep <= 2 && (
                            <div className="hidden sm:block absolute right-0 h-0.5 w-1/3 bg-gray-200" style={{ left: '70%' }} aria-hidden="true" />
                        )}
                    </li>

                    <li className="relative flex items-center sm:w-1/2">
                        <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full ${currentStep === 3
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white border-2 border-gray-300 text-gray-500'
                                }`}
                        >
                            <span>2</span>
                        </div>
                        <span className={`ml-3 text-sm font-medium ${currentStep === 3 ? 'text-indigo-600' : 'text-gray-500'
                            }`}>
                            Finalisation
                        </span>
                    </li>
                </ol>
            </nav>

            {/* Main content */}
            <div className="mt-8 md:mt-0">
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <div className="space-y-8">
                        {renderStep()}

                        <div className="flex justify-between pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                {currentStep === 2 ? 'Annuler' : 'Retour'}
                            </button>
                            <button
                                type="button"
                                onClick={currentStep === 3 ? () => setShowConfirmModal(true) : handleNext}
                                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                                    }`}
                            >
                                {currentStep === 3 ? "Mettre à jour l'échange" : 'Continuer'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Product selection modal */}
            <ProductSelectionModal
                isOpen={showAddProductModal}
                onClose={() => setShowAddProductModal(false)}
                onProductSelect={handleProductSelect}
                allowOutOfStock={false}
            />

            {/* Confirmation modal */}
            <Modal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                title="Confirmer la modification"
                onSubmit={handleSubmitExchange}
                submitLabel={isSubmitting ? "En cours..." : "Confirmer"}
                isSubmitting={isSubmitting}
                cancelLabel="Annuler"
                size="md"
            >
                <div className="py-2">
                    <p className="text-sm text-gray-500">
                        Êtes-vous sûr de vouloir mettre à jour cet échange? Cette action modifiera les informations existantes.
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default EditExchangeForm;